import { callApiWithAuth } from 'api/utils'

import { PATHS } from '../api/paths'
import { getTogetherPath } from '../api/utils'
import { MAX_IMAGE_UPLOADS } from '../interfaces/constants'
import {
  encodePostContent,
  getMentionIdsFromString,
  uploadAndGetSrc,
} from './post'

export const BANNED_STATUS = ['SYSTEM_WITHHELD', 'PREMOD', 'REJECTED']
export const BANNED_MESSAGE_INTL_ID: { [key: string]: string } = {
  SYSTEM_WITHHELD: 'notification.comment.systemWithheld',
  PREMOD: 'notification.comment.premod',
  REJECTED: 'notification.comment.rejected',
}

export const getCommentFormBody = async ({
  postId,
  message,
  photos,
  link,
  locale,
  replyingUserId,
  replyingUserName,
  replyingId,
  isAdmin,
}: {
  locale: string
  postId: string
  message: string
  photos?: (File | string)[]
  link?: string

  replyingId?: string
  replyingUserId?: string
  replyingUserName?: string
  isAdmin?: boolean
}) => {
  const formData = new FormData()
  let submitText = encodePostContent(message.replace(/(\&nbsp;)+/g, ' '))
  const mentionIds = getMentionIdsFromString(submitText)
  if (photos && photos.length > 0) {
    const imageSrcs = await Promise.all(
      photos?.map((file) => uploadAndGetSrc(file, locale, isAdmin)),
    )
    imageSrcs
      .filter(Boolean)
      .slice(0, MAX_IMAGE_UPLOADS)
      .forEach((photo) => {
        formData.append('images[]', photo)
      })
  }
  if (link) formData.append('link', link)
  mentionIds?.forEach((mentionId) => {
    formData.append(`mention_ids[]`, mentionId)
  })
  formData.append('post_id', postId)

  replyingId && formData.append('parent_id', replyingId)
  if (replyingUserId) {
    mentionIds.unshift(replyingUserId)
    submitText = `[mention+id="${replyingUserId}"+name="${(
      replyingUserName || ''
    ).replace('"', "'")}"]${submitText}`
    formData.append('parent_user_id', String(replyingUserId))
  }
  formData.append('content', submitText)
  formData.append('content', submitText)

  return formData
}

export const submitComment = async (
  formData: FormData,
  locale: string,
): Promise<any | null> => {
  return callApiWithAuth(
    getTogetherPath(PATHS.COMMENT.CREATE, {
      _locale: locale,
    }),
    'POST',
    {
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  )
}

export const updateComment = async (
  id: string,
  formData: FormData,
  locale: string,
): Promise<any | null> => {
  return callApiWithAuth(
    getTogetherPath(PATHS.COMMENT.UPDATE, {
      _locale: locale,
      id,
    }),
    'POST',
    {
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  )
}

export type CommentBodyData = {
  text: string
  replyingTo?: string
}

export const decodeCommentBody = (s: string): CommentBodyData => {
  try {
    const authorRegex = s.match(/\[@([^\]]+)\]/)
    const replyingTo = authorRegex
      ? authorRegex[0].replace('[@', '').replace(']', '')
      : ''
    const text = s.replace(/\[@([^\]]+)\]/, '')
    return {
      text: text.trim(),
      replyingTo,
    }
  } catch (e) {
    console.error(e)
  }
  return {
    text: '',
  }
}

export const submitReply = async (
  formData: FormData,
  locale: string,
): Promise<any | null> => {
  return callApiWithAuth(
    getTogetherPath(PATHS.COMMENT.REPLY, {
      _locale: locale,
    }),
    'POST',
    {
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  )
}

export const likeUnlikeComment = async ({
  commentId,
  setLike = false,
  locale,
}: {
  commentId: string
  setLike?: boolean
  locale: string
}) => {
  return callApiWithAuth(
    getTogetherPath(setLike ? PATHS.COMMENT.LIKE : PATHS.COMMENT.UNLIKE, {
      _locale: locale,
      id: commentId,
    }),
    'GET',
  )
}

export const deleteComment = ({
  id,
  locale,
}: {
  id: string
  locale: string
}) => {
  return callApiWithAuth(
    getTogetherPath(PATHS.COMMENT.DELETE, {
      _locale: locale,
      id: id,
    }),
    'DELETE',
  )
}
